import React, { useEffect, useMemo, useState } from "react";
import { BrowserRouter, Routes, Route, Link, NavLink, useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import {
  FilePlus2, FileText, Settings, LogOut, Search, Copy, CheckCircle2, XCircle,
  AlertTriangle, Printer, Download, Archive, Ban, Trash2, ExternalLink, Home, ShieldCheck,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const dcApi = axios.create({ baseURL: `${API}/dc`, withCredentials: true });
dcApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("img_admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const adminApi = axios.create({ baseURL: `${API}/admin`, withCredentials: true });
adminApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("img_admin_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

function useAuth() {
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  useEffect(() => {
    let alive = true;
    adminApi.get("/me").then(r => { if (alive) setMe(r.data); })
      .catch(() => setMe(null)).finally(() => alive && setChecking(false));
    return () => { alive = false; };
  }, []);
  return { me, checking };
}

function StatusPill({ status }) {
  const map = {
    draft: { bg: "bg-slate-100", text: "text-slate-700", label: "Draft" },
    issued: { bg: "bg-teal/10", text: "text-teal", label: "Issued" },
    archived: { bg: "bg-amber-100", text: "text-amber-700", label: "Archived" },
    cancelled: { bg: "bg-red-100", text: "text-red-700", label: "Cancelled" },
  };
  const s = map[status] || map.draft;
  return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>{s.label}</span>;
}

function Layout({ children }) {
  const nav = [
    { to: "/dc", label: "Certificates", icon: FileText, end: true },
    { to: "/dc/new", label: "Issue new", icon: FilePlus2 },
    { to: "/dc/settings", label: "Settings", icon: Settings },
  ];
  const [me, setMe] = useState(null);
  useEffect(() => {
    let alive = true;
    adminApi.get("/me").then(r => alive && setMe(r.data)).catch(() => {});
    return () => { alive = false; };
  }, []);
  const logout = async () => {
    try { await adminApi.post("/logout"); } catch (_) {}
    localStorage.removeItem("img_admin_token");
    window.location.href = "/admin/login?next=/dc";
  };
  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="dc-layout">
      <aside className="w-60 bg-navy text-white flex flex-col">
        <div className="p-5 border-b border-white/10">
          <Link to="/dc" className="flex items-center gap-2 font-display font-bold text-lg">
            <ShieldCheck className="h-5 w-5 text-teal-light" /> DC Module
          </Link>
          <p className="text-[10px] text-white/50 uppercase tracking-[0.22em] mt-1">Infinity Medical Center</p>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {nav.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`dc-nav-${n.label.toLowerCase().replace(/\s/g,'-')}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm ${isActive ? "bg-white/10 text-teal-light" : "text-white/75 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          {me && (
            <div className="px-3 py-2 rounded-lg bg-white/5" data-testid="dc-signed-in-as">
              <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-teal-light font-semibold">
                <span className="h-1.5 w-1.5 rounded-full bg-teal-light inline-block" aria-hidden />
                Signed in
              </div>
              <div className="text-sm text-white mt-0.5 truncate" title={me.email}>{me.full_name || me.email}</div>
              <div className="text-[11px] text-white/50 truncate" title={me.email}>{me.email}</div>
            </div>
          )}
          <Link to="/admin" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-white/70 hover:text-white hover:bg-white/5"><Home className="h-4 w-4" /> IMG Admin</Link>
          <button onClick={logout} className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-red-200 hover:text-white hover:bg-red-500/20 border border-red-400/20" data-testid="dc-logout"><LogOut className="h-4 w-4" /> Log out</button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
      <Toaster richColors position="top-right" />
    </div>
  );
}

function AssetsBanner({ assets }) {
  if (!assets || assets.is_configured) return null;
  return (
    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 flex items-start gap-3 mb-6" data-testid="dc-assets-warning">
      <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
      <div className="flex-1">
        <p className="font-semibold text-amber-900">Certificate assets not configured</p>
        <p className="text-sm text-amber-800 mt-0.5">Upload the doctor's signature and the official medical center stamp before you can issue any certificate.</p>
      </div>
      <Link to="/dc/settings" className="px-4 py-2 rounded-lg bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700">Open settings</Link>
    </div>
  );
}

function Dashboard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        dcApi.get("", { params: { q, status: status || undefined, page: 1, page_size: 100 } }),
        dcApi.get("/assets"),
      ]);
      setItems(r1.data.items || []);
      setTotal(r1.data.total || 0);
      setAssets(r2.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, status]);

  const copyLink = async (uid) => {
    const url = `${window.location.origin}/dc/verify/${uid}`;
    await navigator.clipboard.writeText(url);
    toast.success("Verification link copied");
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl text-navy-800">Death Certificates</h1>
          <p className="text-sm text-navy-500 mt-1">{total} total · Sequence next: <span className="font-mono text-navy-800">IMG/IMC/05-Q/DC/{String((items[0]?.sequence || 1157) + 1).padStart(5, "0")}</span></p>
        </div>
        <Link to="/dc/new" data-testid="dc-new-cta" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-navy text-white font-semibold hover:bg-teal transition-colors">
          <FilePlus2 className="h-4 w-4" /> Issue New Certificate
        </Link>
      </div>

      <AssetsBanner assets={assets} />

      <div className="bg-white rounded-2xl border border-slate-200 p-4 mb-4 flex gap-3 items-center">
        <div className="relative flex-1">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search cert # or deceased name..." className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="dc-search" />
        </div>
        <select value={status} onChange={e => setStatus(e.target.value)} data-testid="dc-status-filter" className="px-3 py-2 border border-slate-200 rounded-lg text-sm">
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="issued">Issued</option>
          <option value="archived">Archived</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-4 py-3">Cert #</th><th className="text-left px-4 py-3">Deceased</th><th className="text-left px-4 py-3">DoD</th><th className="text-left px-4 py-3">Issued</th><th className="text-left px-4 py-3">Status</th><th className="text-left px-4 py-3">Actions</th></tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center p-8 text-slate-400">Loading…</td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="text-center p-10 text-slate-400" data-testid="dc-empty">No certificates yet. Click "Issue New Certificate" to create the first draft.</td></tr>
              : items.map(c => (
                <tr key={c.id} data-testid={`dc-row-${c.id}`} className="border-t border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-mono text-xs">{c.certificate_number || <span className="text-slate-400">— pending —</span>}</td>
                  <td className="px-4 py-3 font-semibold text-navy-800">{c.deceased_full_name}</td>
                  <td className="px-4 py-3 text-slate-600">{c.date_of_death}</td>
                  <td className="px-4 py-3 text-slate-600">{c.date_of_issue || "—"}</td>
                  <td className="px-4 py-3"><StatusPill status={c.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button onClick={() => nav(`/dc/${c.id}`)} className="text-xs px-2.5 py-1.5 rounded-md bg-navy text-white hover:bg-teal">View</button>
                      {c.status === "issued" && c.verification_uid && <button onClick={() => copyLink(c.verification_uid)} className="text-xs px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-teal text-slate-600" data-testid={`dc-copy-link-${c.id}`}><Copy className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const EMPTY = {
  deceased_full_name: "", mother_name: "", date_of_birth: "", place_of_birth: "", sex: "male",
  nationality: "", residential_address: "", date_of_death: "", time_of_death: "", place_of_death: "",
  age_at_death: "", medical_history: "", medical_findings: "", immediate_cause: "",
  underlying_cause: "", other_significant_conditions: "", remarks: "",
  doctor_name: "", doctor_license: "",
};

function Field({ label, children, required, ...p }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-navy-700 uppercase tracking-wider">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function CertForm({ cert = null, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY, ...(cert || {}) });
  const [saving, setSaving] = useState(false);
  const [issueOpen, setIssueOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [assets, setAssets] = useState(null);
  const nav = useNavigate();

  useEffect(() => { dcApi.get("/assets").then(r => setAssets(r.data)); }, []);
  useEffect(() => {
    // Pre-fill doctor from assets defaults if new
    if (!cert && assets && !form.doctor_name) {
      setForm(f => ({ ...f, doctor_name: assets.doctor_name || "", doctor_license: assets.doctor_license || "" }));
    }
    // eslint-disable-next-line
  }, [assets]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const computedAge = useMemo(() => {
    if (form.age_at_death) return form.age_at_death;
    if (form.date_of_birth && form.date_of_death) {
      try {
        const b = new Date(form.date_of_birth), d = new Date(form.date_of_death);
        return d.getFullYear() - b.getFullYear() - ((d.getMonth() < b.getMonth() || (d.getMonth() === b.getMonth() && d.getDate() < b.getDate())) ? 1 : 0);
      } catch (_) { return ""; }
    }
    return "";
  }, [form.date_of_birth, form.date_of_death, form.age_at_death]);

  const save = async (thenIssue) => {
    if (!form.deceased_full_name || !form.date_of_death) {
      toast.error("Deceased full name and date of death are required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, age_at_death: form.age_at_death ? Number(form.age_at_death) : (computedAge || null) };
      let id = cert?.id;
      if (id) {
        const r = await dcApi.patch(`/${id}`, payload);
        id = r.data.id;
      } else {
        const r = await dcApi.post("", payload);
        id = r.data.id;
      }
      if (thenIssue) {
        setIssueOpen(true);
        onSaved && onSaved(id);
        if (!cert) nav(`/dc/${id}/edit?issue=1`, { replace: true });
      } else {
        // Draft cert → "Draft saved". Issued/archived cert being patched → "Changes saved".
        const isDraftSave = !cert || cert.status === "draft";
        toast.success(isDraftSave ? "Draft saved" : "Changes saved");
        onSaved && onSaved(id);
        if (!cert) nav(`/dc/${id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const confirmIssue = async () => {
    if (confirmText.trim().toUpperCase() !== "ISSUE") {
      toast.error("Type ISSUE to confirm");
      return;
    }
    try {
      const r = await dcApi.post(`/${cert.id}/issue`);
      toast.success(`Certificate ${r.data.certificate_number} issued`);
      setIssueOpen(false);
      nav(`/dc/${cert.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issue failed");
    }
  };

  const nextPreview = assets && (assets.next_sequence || "01158");

  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dc" className="text-sm text-navy-500 hover:text-teal">← Back</Link>
      </div>
      <h1 className="font-display font-bold text-3xl text-navy-800 mb-2">{cert ? "Edit certificate" : "Issue new certificate"}</h1>
      <p className="text-sm text-navy-500 mb-6">All fields you enter will appear on the printable PDF.</p>

      <AssetsBanner assets={assets} />

      <div className="space-y-6">
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-semibold text-navy-800 mb-4 text-sm uppercase tracking-wider">Deceased information</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Full name" required><input data-testid="dc-form-name" value={form.deceased_full_name} onChange={e => set("deceased_full_name", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Mother's name"><input value={form.mother_name} onChange={e => set("mother_name", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Date of birth"><input type="date" value={form.date_of_birth || ""} onChange={e => set("date_of_birth", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Place of birth"><input value={form.place_of_birth} onChange={e => set("place_of_birth", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Sex"><select value={form.sex} onChange={e => set("sex", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"><option value="male">Male</option><option value="female">Female</option><option value="other">Other</option></select></Field>
            <Field label="Nationality"><input value={form.nationality} onChange={e => set("nationality", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Residential address" className="col-span-2"><input value={form.residential_address} onChange={e => set("residential_address", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-semibold text-navy-800 mb-4 text-sm uppercase tracking-wider">Death information</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Date of death" required><input data-testid="dc-form-dod" type="date" value={form.date_of_death || ""} onChange={e => set("date_of_death", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Time of death"><input type="time" value={form.time_of_death || ""} onChange={e => set("time_of_death", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Place of death"><input value={form.place_of_death} onChange={e => set("place_of_death", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Age at death (auto)"><input type="number" value={form.age_at_death || computedAge || ""} onChange={e => set("age_at_death", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" placeholder={String(computedAge || "")} /></Field>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-semibold text-navy-800 mb-4 text-sm uppercase tracking-wider">Medical / clinical information</h2>
          <div className="space-y-3">
            <Field label="Medical history"><textarea rows={3} value={form.medical_history} onChange={e => set("medical_history", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" /></Field>
            <Field label="Clinical findings"><textarea rows={3} value={form.medical_findings} onChange={e => set("medical_findings", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Immediate cause"><input data-testid="dc-form-immediate" value={form.immediate_cause} onChange={e => set("immediate_cause", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" /></Field>
              <Field label="Underlying cause"><input value={form.underlying_cause} onChange={e => set("underlying_cause", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" /></Field>
            </div>
            <Field label="Other significant conditions"><textarea rows={2} value={form.other_significant_conditions} onChange={e => set("other_significant_conditions", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" /></Field>
            <Field label="Additional remarks"><textarea rows={2} value={form.remarks} onChange={e => set("remarks", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
          </div>
        </section>

        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h2 className="font-semibold text-navy-800 mb-4 text-sm uppercase tracking-wider">Issuance information</h2>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Certificate number"><input readOnly value={cert?.certificate_number || "Will be auto-assigned on issue"} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500 font-mono" /></Field>
            <Field label={cert && cert.status !== "draft" ? "Date of issue (editable)" : "Date of issue"}>
              {cert && cert.status !== "draft" ? (
                <input
                  type="date"
                  data-testid="dc-form-date-of-issue"
                  value={form.date_of_issue || ""}
                  onChange={e => set("date_of_issue", e.target.value)}
                  className="w-full px-3 py-2 border border-teal/40 rounded-lg text-sm bg-white text-navy-800"
                  title="Editable post-issuance. Every change is logged in the audit trail."
                />
              ) : (
                <input readOnly value={cert?.date_of_issue || "Today (when issued)"} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-slate-50 text-slate-500" />
              )}
            </Field>
            <Field label="Doctor name"><input value={form.doctor_name} onChange={e => set("doctor_name", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
            <Field label="Doctor SMA license"><input value={form.doctor_license} onChange={e => set("doctor_license", e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button onClick={() => save(false)} disabled={saving} data-testid="dc-form-save-draft" className="px-5 py-2.5 rounded-lg border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50">{cert && cert.status !== "draft" ? "Save changes" : "Save as Draft"}</button>
          {cert && cert.status === "draft" && (
            <button onClick={() => setIssueOpen(true)} disabled={!assets?.is_configured} data-testid="dc-form-issue" title={!assets?.is_configured ? "Configure signature + stamp at /dc/settings first" : ""} className="px-5 py-2.5 rounded-lg bg-navy text-white font-semibold hover:bg-teal disabled:opacity-50 disabled:cursor-not-allowed">Issue Certificate</button>
          )}
        </div>
      </div>

      {issueOpen && cert && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" data-testid="dc-issue-modal">
            <h3 className="font-display font-bold text-xl text-navy-800">Confirm issuance</h3>
            <p className="text-sm text-navy-500 mt-2">This certificate will be permanently issued. Numbering will assign automatically.</p>
            <div className="mt-4 space-y-1 text-sm bg-slate-50 rounded-lg p-3">
              <p><span className="text-slate-500">Deceased:</span> <strong>{cert.deceased_full_name}</strong></p>
              <p><span className="text-slate-500">Date of death:</span> {cert.date_of_death}</p>
            </div>
            <p className="text-xs text-navy-500 mt-4">Type <strong className="text-navy-800">ISSUE</strong> to confirm:</p>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} data-testid="dc-issue-confirm-input" className="w-full mt-2 px-3 py-2 border border-slate-200 rounded-lg text-sm font-mono" autoFocus />
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setIssueOpen(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm">Cancel</button>
              <button onClick={confirmIssue} data-testid="dc-issue-confirm" className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-teal">Issue certificate</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NewCert() {
  return <CertForm />;
}

function EditCert() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  useEffect(() => { dcApi.get(`/${id}`).then(r => setCert(r.data)); }, [id]);
  if (!cert) return <div className="p-8 text-navy-500">Loading…</div>;
  return <CertForm cert={cert} />;
}

function CertDetail() {
  const { id } = useParams();
  const [cert, setCert] = useState(null);
  const [assets, setAssets] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [r1, r2] = await Promise.all([dcApi.get(`/${id}`), dcApi.get("/assets")]);
    setCert(r1.data);
    setAssets(r2.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!cert) return <div className="p-8 text-navy-500">Loading…</div>;
  const verifyUrl = cert.verification_uid ? `${window.location.origin}/dc/verify/${cert.verification_uid}` : null;

  const openPdf = () => window.open(`${API}/dc/${id}/pdf`, "_blank");
  const doAction = async (action) => {
    setBusy(true);
    try {
      if (action === "cancel") {
        const reason = window.prompt("Reason for cancellation:");
        if (!reason) { setBusy(false); return; }
        await dcApi.post(`/${id}/cancel`, { reason });
      } else if (action === "archive") {
        await dcApi.post(`/${id}/archive`);
      } else if (action === "delete") {
        if (!window.confirm("Permanently delete this draft?")) { setBusy(false); return; }
        await dcApi.delete(`/${id}`);
        toast.success("Draft deleted");
        nav("/dc"); return;
      } else if (action === "issue") {
        nav(`/dc/${id}/edit?issue=1`);
        return;
      }
      toast.success("Done");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-5xl mx-auto p-8">
      <div className="flex items-center gap-3 mb-4">
        <Link to="/dc" className="text-sm text-navy-500 hover:text-teal">← Back</Link>
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <StatusPill status={cert.status} />
            <h1 className="font-display font-bold text-3xl text-navy-800 mt-3">{cert.deceased_full_name}</h1>
            <p className="font-mono text-sm text-navy-500 mt-1">{cert.certificate_number || "— pending issuance —"}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {cert.status === "issued" && <button onClick={openPdf} data-testid="dc-detail-pdf" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-teal"><Download className="h-4 w-4" /> PDF</button>}
            {verifyUrl && <a href={verifyUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-navy-800 text-sm font-semibold hover:border-teal"><ExternalLink className="h-4 w-4" /> Public verify</a>}
            {cert.status === "draft" && <button onClick={() => nav(`/dc/${id}/edit`)} data-testid="dc-detail-edit-draft" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-navy-800 text-sm font-semibold hover:border-teal">Edit</button>}
            {(cert.status === "issued" || cert.status === "archived") && <button onClick={() => nav(`/dc/${id}/edit`)} data-testid="dc-detail-edit-issued" title="Post-issuance edits are audited" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-teal/40 text-navy-800 text-sm font-semibold hover:bg-teal/5">Edit</button>}
            {cert.status === "draft" && <button onClick={() => doAction("issue")} disabled={!assets?.is_configured || busy} title={!assets?.is_configured ? "Configure signature + stamp first" : ""} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal text-white text-sm font-semibold hover:bg-navy disabled:opacity-40 disabled:cursor-not-allowed">Issue certificate</button>}
            {cert.status === "issued" && <button onClick={() => doAction("archive")} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-slate-200 text-navy-800 text-sm hover:border-teal"><Archive className="h-4 w-4" /> Archive</button>}
            {(cert.status === "issued" || cert.status === "archived") && <button onClick={() => doAction("cancel")} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm hover:bg-red-50"><Ban className="h-4 w-4" /> Cancel</button>}
            {cert.status === "draft" && <button onClick={() => doAction("delete")} disabled={busy} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-red-200 text-red-700 text-sm hover:bg-red-50"><Trash2 className="h-4 w-4" /> Delete</button>}
          </div>
        </div>
        {verifyUrl && (
          <div className="mt-4 flex items-center gap-2 text-xs bg-slate-50 rounded-lg p-2">
            <span className="text-slate-500">Verify URL:</span>
            <code className="flex-1 truncate text-teal">{verifyUrl}</code>
            <button onClick={() => { navigator.clipboard.writeText(verifyUrl); toast.success("Copied"); }} className="px-2 py-1 rounded bg-white border border-slate-200 hover:border-teal"><Copy className="h-3.5 w-3.5" /></button>
          </div>
        )}
      </div>

      <div className="grid gap-4">
        {[
          ["Deceased", [["Full name", cert.deceased_full_name], ["Mother's name", cert.mother_name], ["Date of birth", cert.date_of_birth], ["Place of birth", cert.place_of_birth], ["Sex", cert.sex], ["Nationality", cert.nationality], ["Residence", cert.residential_address]]],
          ["Death", [["Date of death", cert.date_of_death], ["Time of death", cert.time_of_death], ["Place of death", cert.place_of_death], ["Age at death", cert.age_at_death]]],
          ["Medical", [["Medical history", cert.medical_history], ["Clinical findings", cert.medical_findings], ["Immediate cause", cert.immediate_cause], ["Underlying cause", cert.underlying_cause], ["Other conditions", cert.other_significant_conditions], ["Remarks", cert.remarks]]],
          ["Issuance", [["Certificate #", cert.certificate_number], ["Date of issue", cert.date_of_issue], ["Doctor", cert.issued_doctor_name || cert.doctor_name], ["License", cert.issued_doctor_license || cert.doctor_license], ["Issued by", cert.issued_by_name]]],
        ].map(([title, rows]) => (
          <section key={title} className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-xs uppercase tracking-wider text-teal mb-3">{title}</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {rows.map(([k, v]) => (<div key={k} className="flex gap-3"><dt className="text-slate-500 w-40 shrink-0">{k}</dt><dd className="text-navy-800 font-medium">{v || <span className="text-slate-400">—</span>}</dd></div>))}
            </dl>
          </section>
        ))}
        <section className="bg-white rounded-2xl border border-slate-200 p-6">
          <h3 className="font-semibold text-xs uppercase tracking-wider text-teal mb-3">Audit history</h3>
          <ul className="text-sm space-y-2">
            {(cert.history || []).map((h, i) => (<li key={i} className="flex gap-3 items-baseline"><code className="text-xs text-slate-400 w-40">{h.at}</code><span className="font-semibold text-navy-800">{h.action}</span><span className="text-slate-600">by {h.actor}</span>{h.reason && <span className="text-red-600">— {h.reason}</span>}</li>))}
          </ul>
        </section>
      </div>
    </div>
  );
}

function MediaCard({ title, subtitle, asset, onUpload, onReset, allowReset = false, testId }) {
  const [uploading, setUploading] = useState(false);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("make_transparent_flag", "1");
      const t = localStorage.getItem("img_admin_token");
      const r = await axios.post(`${API}/admin/media/upload`, fd, {
        headers: { Authorization: `Bearer ${t}` },
      });
      onUpload(r.data.id);
      toast.success("Uploaded — click Save to apply");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };
  const backend = process.env.REACT_APP_BACKEND_URL;
  const src = asset?.url ? (asset.url.startsWith("http") ? asset.url : `${backend}${asset.url}`) : null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={testId}>
      <h3 className="font-semibold text-navy-800">{title}</h3>
      <p className="text-xs text-navy-500 mt-1">{subtitle}</p>
      <div className="mt-4 aspect-video bg-slate-50 border border-dashed border-slate-200 rounded-lg flex items-center justify-center overflow-hidden">
        {src ? <img src={src} alt={title} className="max-h-full max-w-full object-contain" /> : <span className="text-sm text-slate-400">Not configured</span>}
      </div>
      <div className="mt-4 flex gap-2">
        <label className="flex-1 text-center px-3 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:bg-teal cursor-pointer">
          {uploading ? "Uploading…" : (src ? "Replace" : "Upload")}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={handleFile} data-testid={`${testId}-file`} />
        </label>
        {allowReset && <button onClick={onReset} className="px-3 py-2 rounded-lg border border-slate-200 text-slate-700 text-sm hover:border-teal">Reset</button>}
      </div>
    </div>
  );
}

function DcSettings() {
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const load = () => dcApi.get("/assets").then(r => { setState(r.data); setDraft({}); });
  useEffect(() => { load(); }, []);
  const merged = { ...state, ...draft };
  const save = async () => {
    setSaving(true);
    try {
      await dcApi.put("/assets", draft);
      toast.success("Saved");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  if (!state) return <div className="p-8 text-navy-500">Loading…</div>;
  const setAsset = (k, id) => setDraft(d => ({ ...d, [k]: id }));
  return (
    <div className="max-w-5xl mx-auto p-8">
      <h1 className="font-display font-bold text-3xl text-navy-800 mb-2">Certificate settings</h1>
      <p className="text-sm text-navy-500 mb-6">Configure the assets used on every death certificate PDF. Changes only apply to certificates issued AFTER saving (historical certificates keep their original assets).</p>

      <div className="grid gap-5 md:grid-cols-3">
        <MediaCard title="Doctor signature" subtitle="PNG/JPG on white — background will be removed automatically." asset={state.doctor_signature ? { url: state.doctor_signature.url } : null} onUpload={id => setAsset("doctor_signature_media_id", id)} testId="dc-settings-signature" />
        <MediaCard title="Medical center stamp" subtitle="Round rubber stamp — PNG/JPG, background stripped on upload." asset={state.medical_center_stamp ? { url: state.medical_center_stamp.url } : null} onUpload={id => setAsset("medical_center_stamp_media_id", id)} testId="dc-settings-stamp" />
        <MediaCard title="Certificate logo" subtitle="IMG horizontal or emblem logo — used at top of certificate." asset={state.certificate_logo ? { url: state.certificate_logo.url } : null} onUpload={id => setAsset("certificate_logo_media_id", id)} allowReset onReset={() => setAsset("certificate_logo_media_id", null)} testId="dc-settings-logo" />
      </div>

      <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="font-semibold text-navy-800 mb-4">Certifying doctor</h3>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Doctor name"><input defaultValue={state.doctor_name} onChange={e => setDraft(d => ({ ...d, doctor_name: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
          <Field label="SMA license"><input defaultValue={state.doctor_license} onChange={e => setDraft(d => ({ ...d, doctor_license: e.target.value }))} className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm" /></Field>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <button onClick={save} disabled={saving || Object.keys(draft).length === 0} data-testid="dc-settings-save" className="px-6 py-2.5 rounded-lg bg-navy text-white font-semibold hover:bg-teal disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
      </div>
    </div>
  );
}

function AdminGate({ children }) {
  const { me, checking } = useAuth();
  if (checking) return <div className="min-h-screen flex items-center justify-center text-navy-500">Loading…</div>;
  if (!me) {
    window.location.href = "/admin/login?next=/dc";
    return null;
  }
  return children;
}

// PUBLIC verify page — accessible without auth at /dc/verify/:key
export function DcVerify() {
  const { key } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    axios.get(`${API}/dc/verify/${encodeURIComponent(key)}`)
      .then(r => setState({ loading: false, data: r.data }))
      .catch(() => setState({ loading: false, data: null }));
  }, [key]);

  useEffect(() => {
    document.title = "Certificate Verification | Infinity Medical Group";
    let m = document.querySelector('meta[name="robots"]');
    if (!m) {
      m = document.createElement("meta");
      m.setAttribute("name", "robots");
      document.head.appendChild(m);
    }
    m.setAttribute("content", "noindex, nofollow");
  }, []);

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-400">Verifying…</div>;

  const d = state.data;
  const notFound = !d;
  const cancelled = d && d.status === "cancelled";
  const valid = d && d.is_valid;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="dc-verify-page">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto p-4 flex items-center gap-3">
          <ShieldCheck className="h-6 w-6 text-teal" />
          <span className="font-display font-bold text-navy-800">Infinity Medical Group</span>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500">Certificate Verification</span>
        </div>
      </header>
      <main className="max-w-3xl mx-auto p-6 print:p-0">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 print:shadow-none print:border-0 relative overflow-hidden">
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center opacity-[0.04]">
            <ShieldCheck className="h-96 w-96 text-teal" />
          </div>
          <div className="relative">
            {notFound ? (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold" data-testid="verify-status-notfound"><XCircle className="h-4 w-4" /> NOT FOUND</div>
                <h1 className="font-display font-bold text-3xl text-navy-800 mt-4">Certificate not found</h1>
                <p className="text-slate-600 mt-2 max-w-lg">The verification link you followed does not match any certificate in our records. If you believe this is an error, please contact Infinity Medical Center.</p>
                <a href="https://infinitymg.so" className="mt-6 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">Go to Infinity Medical Group <ExternalLink className="h-4 w-4" /></a>
              </>
            ) : cancelled ? (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-700 text-xs font-semibold" data-testid="verify-status-cancelled"><Ban className="h-4 w-4" /> CANCELLED — NO LONGER VALID</div>
                <h1 className="font-display font-bold text-3xl text-navy-800 mt-4">This certificate has been cancelled</h1>
                <VerifyBody d={d} />
              </>
            ) : (
              <>
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal/10 text-teal text-xs font-semibold" data-testid="verify-status-valid"><CheckCircle2 className="h-4 w-4" /> VALID{d.status === "archived" ? " · ARCHIVED" : ""}</div>
                <h1 className="font-display font-bold text-3xl text-navy-800 mt-4">Officially verified by Infinity Medical Center</h1>
                <VerifyBody d={d} />
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

function VerifyBody({ d }) {
  const c = d.issuing_center || {};
  return (
    <>
      <dl className="mt-6 grid gap-2 text-sm">
        <Row label="Certificate number" value={d.certificate_number} mono />
        <Row label="Deceased" value={d.deceased_full_name} strong />
        <Row label="Date of death" value={d.date_of_death} />
        <Row label="Date of issue" value={d.date_of_issue} />
      </dl>
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wider text-teal">Issuing medical center</h3>
          <p className="mt-2 font-semibold text-navy-800">{c.center_name}</p>
          <p className="text-sm text-slate-500">{c.parent}</p>
          <p className="text-sm text-slate-600 mt-2">{c.address}</p>
          <p className="text-sm text-slate-600">{c.phone}</p>
          <p className="text-sm text-slate-600">{c.email}</p>
        </div>
        <div>
          <h3 className="font-semibold text-xs uppercase tracking-wider text-teal">Certifying doctor</h3>
          <p className="mt-2 font-semibold text-navy-800">{d.doctor_name}</p>
          <p className="text-sm text-slate-500">SMA License No. {d.doctor_license}</p>
        </div>
      </div>
      <p className="mt-8 text-xs text-slate-400 print:hidden">Verified at {d.verified_at}</p>
    </>
  );
}
function Row({ label, value, mono, strong }) {
  return (<div className="flex gap-4"><dt className="text-slate-500 w-40 shrink-0">{label}</dt><dd className={`text-navy-800 ${mono ? "font-mono text-xs" : ""} ${strong ? "font-bold text-base" : ""}`}>{value || <span className="text-slate-400">—</span>}</dd></div>);
}

export default function DcApp() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/dc/verify/:key" element={<DcVerify />} />
        <Route path="/dc/*" element={
          <AdminGate>
            <Layout>
              <Routes>
                <Route path="/" element={<Dashboard />} />
                <Route path="new" element={<NewCert />} />
                <Route path=":id" element={<CertDetail />} />
                <Route path=":id/edit" element={<EditCert />} />
                <Route path="settings" element={<DcSettings />} />
              </Routes>
            </Layout>
          </AdminGate>
        } />
      </Routes>
    </BrowserRouter>
  );
}
